import {
    ApproveExpenseGet,
    ApproveInvoiceGet,
    SubscribeState,
} from "@kase/my-marathon-communication";
import { ReadonlySignal, computed, signal } from "@preact/signals";
import { store } from "~/core/store";
import { upsert } from "~/utils";
import { loadThumbnails } from "../expense/attachment";
import { ApiSliceParams } from "../interfaces";
import {
    createEmitter,
    createListController,
    deferDocument,
    stubDocument,
} from "../utils";
import { makeAccounting } from "./accounting";
import { makeApproval } from "./approval";
import {
    ApproveEntityType,
    ApproveExpense,
    ApproveFilter,
    ApproveInvoice,
    ApproveInvoiceCost,
    ApproveInvoicePurchase,
    ApproveInvoiceRow,
    ApprovePrivateFilter,
    SubscriptionsState,
} from "./interfaces";
import {
    getEntityId,
    getFilterKey,
    getPrivateFilterKey,
    getRowId,
    loadExpenseAttachments,
    splitCompanies,
} from "./utils";

export const makeApprove = ({ connection, company }: ApiSliceParams) => {
    const accounting = makeAccounting({ connection, company });
    const approval = makeApproval({ connection, company });
    const previewState: Record<string, boolean> = {};
    const subscriptions: SubscriptionsState = {
        invoice: {},
        expense: {},
    };
    const expenseLoadedState: Record<string, boolean> = {};
    const loadingStore: Record<string, ReadonlySignal<boolean>> = {};
    const loadedStore: Record<string, ReadonlySignal<boolean>> = {};
    const expenseEmitter = createEmitter<ApproveExpense>();
    const invoiceEmitter = createEmitter<ApproveInvoice>();

    const getBase = () => ({
        document: null,
        documentTag: signal(0),
        loading: signal(false),
        loaded: signal(false),
    });

    const {
        items: expenses,
        listen: listenExpenses,
        loading: loadingExpenses,
        loaded: loadedExpenses,
    } = createListController<
        ApproveExpenseGet,
        ApproveExpense,
        ApprovePrivateFilter
    >({
        connection,
        list: (con, { status, companies }) =>
            con.approve.expense.itemsBatch({ status, company: companies }),
        getKey: getPrivateFilterKey,
        getId: (e) => getEntityId("expense", e),
        view: (item) => ({
            ...item,
            ...getBase(),
            _id: item.id,
            id: getEntityId("expense", item),
            receipts: signal([]),
            approvals: computed(
                () =>
                    approval.expenses.value.filter(
                        (m) =>
                            m.expense.equals(item.id) &&
                            m.company === item.company,
                    ) ?? null,
            ),
            mine: computed(
                () =>
                    approval.mine.expenses.value.find(
                        (m) =>
                            m.expense.equals(item.id) &&
                            m.company === item.company,
                    ) ?? null,
            ),
        }),
        emit: expenseEmitter.emit,
        name: "approveExpense",
    });

    const {
        items: invoices,
        listen: listenInvoices,
        loading: loadingInvoices,
        loaded: loadedInvoices,
    } = createListController<
        ApproveInvoiceGet,
        ApproveInvoice,
        ApprovePrivateFilter
    >({
        connection,
        list: (con, { status, companies }) =>
            con.approve.invoice.itemsBatch({ status, company: companies }),
        getKey: getPrivateFilterKey,
        getId: (e) => getEntityId("invoice", e),
        view: (item) => {
            const rows = signal<ApproveInvoiceRow[]>([]);

            return {
                ...item,
                ...getBase(),
                _id: item.id,
                id: getEntityId("invoice", item),
                approvals: computed(
                    () =>
                        approval.invoices.value.filter(
                            (m) =>
                                m.invoice.equals(item.id) &&
                                m.company === item.company,
                        ) ?? null,
                ),
                rows,
                costs: computed(() =>
                    rows.value.filter(
                        (row): row is ApproveInvoiceCost =>
                            row.kind?.$case === "cost",
                    ),
                ),
                purchases: computed(() =>
                    rows.value.filter(
                        (row): row is ApproveInvoicePurchase =>
                            row.kind?.$case === "purchase",
                    ),
                ),
                mine: computed(
                    () =>
                        approval.mine.invoices.value.find(
                            (m) =>
                                m.invoice.equals(item.id) &&
                                m.company === item.company,
                        ) ?? null,
                ),
            };
        },
        emit: invoiceEmitter.emit,
        name: "approveInvoice",
    });

    const all = computed(() => [...expenses.value, ...invoices.value]);

    const openInvoice = async (invoice: ApproveInvoice) => {
        invoice.loading.value = true;

        const stream = connection.value.approve.invoice.accounting.open({
            id: invoice._id,
            company: invoice.company,
        });

        subscriptions.invoice[invoice.id] = stream;

        for await (const event of stream) {
            if (event.kind === SubscribeState.Remove) {
                invoice.rows.value = invoice.rows
                    .peek()
                    .filter(
                        (r) =>
                            !event.values.find(
                                (v) => getRowId(invoice.id, v) === r.id,
                            ),
                    );
            } else {
                if (event.values.length > 0) {
                    invoice.rows.value = upsert(
                        invoice.rows.peek().slice(),
                        event.values.map((i) => ({
                            ...i,
                            _id: i.id,
                            id: getRowId(invoice.id, i),
                        })),
                        "id",
                    );
                }

                if (
                    event.kind === SubscribeState.LoadedCache ||
                    event.kind === SubscribeState.Loaded
                ) {
                    invoice.loading.value = false;
                    invoice.loaded.value = true;
                }
            }
        }
    };

    const openExpense = async (expense: ApproveExpense) => {
        if (expenseLoadedState[expense.id]) {
            return;
        }

        expenseLoadedState[expense.id] = true;
        expense.loading.value = true;

        const api = store.api;
        const stream = connection.value.expense.open({
            id: expense._id,
            company: expense.company,
        });

        subscriptions.expense[expense.id] = stream;

        for await (const event of stream) {
            if (event.kind === SubscribeState.Remove) {
                const newReceipts = expense.receipts.peek().slice();

                for (const r of event.values) {
                    const idx = newReceipts.findIndex((nr) =>
                        nr._id.equals(r.id),
                    );

                    if (idx !== -1) {
                        newReceipts.splice(idx, 1);
                    }
                }

                expense.receipts.value = newReceipts;
            } else {
                const result = event.values.map((r) =>
                    api.expense.receipt.view(r),
                );
                const newReceipts = upsert(
                    expense.receipts.peek().slice(),
                    result,
                    "id",
                );

                result.forEach((r) =>
                    loadExpenseAttachments(
                        expense.id,
                        r,
                        connection,
                        company,
                        previewState,
                    ),
                );
                expense.receipts.value = newReceipts;
            }

            if (event.kind === SubscribeState.Loaded) {
                expense.loading.value = false;
                expense.loaded.value = true;
            }
        }
    };

    const open = (id: string, type: ApproveEntityType) => {
        if (previewState[id]) {
            return;
        }

        previewState[id] = true;

        if (type === "invoice") {
            const invoice = invoices.peek().find((i) => i.id === id);

            if (invoice) {
                const noDocument = invoice.document === null;

                invoice.document = deferDocument(
                    connection.value.approve.invoice.getDocument({
                        company: company.value,
                        id: invoice._id,
                    }),
                );

                openInvoice(invoice);

                if (noDocument) {
                    invoice.documentTag.value = invoice.documentTag.peek() + 1;
                }
            }
        } else {
            const expense = expenses.peek().find((e) => e.id === id);

            if (expense) {
                openExpense(expense);

                expense.document = stubDocument();
                expense.documentTag.value = expense.documentTag.peek() + 1;
                expense.receipts
                    .peek()
                    .flatMap((r) => r.attachments.peek())
                    .forEach(loadThumbnails);
            }
        }
    };

    const listen = (filter: ApproveFilter) => {
        const [invoiceCompanies, expenseCompanies] = splitCompanies(
            filter.companies,
        );

        filter.statuses.forEach((status) => {
            listenInvoices({ status, companies: invoiceCompanies });
            listenExpenses({ status, companies: expenseCompanies });
            approval.listen(
                { status, companies: invoiceCompanies },
                { status, companies: expenseCompanies },
            );
        });
    };

    const loading = (filter: ApproveFilter) => {
        const key = getFilterKey(filter);

        if (!(key in loadingStore)) {
            const [invoiceCompanies, expenseCompanies] = splitCompanies(
                filter.companies,
            );
            const exp = filter.statuses.map((status) =>
                loadingExpenses({ status, companies: expenseCompanies }),
            );
            const inv = filter.statuses.map((status) =>
                loadingInvoices({ status, companies: invoiceCompanies }),
            );

            loadingStore[key] = computed(() =>
                exp
                    .map((e) => e.value)
                    .concat(inv.map((i) => i.value))
                    .some((v) => v),
            );
        }

        return loadingStore[key];
    };

    const loaded = (filter: ApproveFilter) => {
        const key = getFilterKey(filter);

        if (!(key in loadedStore)) {
            const [invoiceCompanies, expenseCompanies] = splitCompanies(
                filter.companies,
            );
            const exp = filter.statuses.map((status) =>
                loadedExpenses({ status, companies: expenseCompanies }),
            );
            const inv = filter.statuses.map((status) =>
                loadedInvoices({ status, companies: invoiceCompanies }),
            );

            loadedStore[key] = computed(() =>
                exp
                    .map((e) => e.value)
                    .concat(inv.map((i) => i.value))
                    .every((v) => v),
            );
        }

        return loadedStore[key];
    };

    invoiceEmitter.on("remove", (items) =>
        items.forEach(({ id }) => {
            previewState[id] = false;
            subscriptions.invoice[id]?.[Symbol.dispose]();

            delete subscriptions.invoice[id];
        }),
    );
    expenseEmitter.on("remove", (items) =>
        items.forEach((item) => {
            previewState[item.id] = false;
            expenseLoadedState[item.id] = false;
            subscriptions.expense[item.id]?.[Symbol.dispose]();

            delete subscriptions.expense[item.id];
        }),
    );

    return {
        accounting,
        approval,
        all,
        invoices,
        expenses,
        listen,
        loading,
        loaded,
        open,
        onInvoice: invoiceEmitter.on,
        offInvoice: invoiceEmitter.off,
        onExpense: expenseEmitter.on,
        offExpense: expenseEmitter.off,
    };
};
